import React, { useCallback, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { CheckBox, CheckBoxVariant } from "@SeemplicityDev/shared";
import { AuthContext } from "../Auth/AuthContext";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../General/AppPreferencesProvider";
import { GeneralAppContext, THEMES } from "../General/GeneralAppProvider";
import {
  LS_FILTER,
  LS_REMEDIATION_ITEMS_FILTER,
  LS_SCOPE,
} from "../Findings/FindingsFilters/FilterUtils";
import type { AppSettingsMenuItem } from "./appSettingsMenuItem";

interface UseUserMenuItemsReturnValue {
  menuItems: AppSettingsMenuItem[];
  loggedUserLabel: string;
}

const useUserMenuItems = (): UseUserMenuItemsReturnValue => {
  const history = useHistory();
  const { isAllowed } = useContext(AppPreferencesContext);
  const { appContext, updateTheme } = useContext(GeneralAppContext);
  const { currentTheme } = appContext;
  const { customerProvisioning } = useFlags();
  const {
    authState,
    organizations,
    stopImpersonation,
    clearOrgAndAccount,
    logout,
    isImpersonating,
  } = useContext(AuthContext);

  const removeImpersonationFromLS = useCallback(() => {
    stopImpersonation();
    window.localStorage.removeItem("flow"); //init workflows
    window.localStorage.removeItem(LS_SCOPE);
    window.localStorage.removeItem(LS_FILTER);
    window.localStorage.removeItem(LS_REMEDIATION_ITEMS_FILTER);
    window.location.reload();
  }, [stopImpersonation]);

  const hasMultiAccounts = useMemo(
    () => (organizations?.flatMap((org) => org.accounts).length ?? 0) > 1,
    [organizations]
  );

  const handleThemeToggle = useCallback(
    (value: unknown, isChecked: boolean) => {
      updateTheme(isChecked ? THEMES.DARK : THEMES.LIGHT);
    },
    [updateTheme]
  );

  const menuItems = useMemo(() => {
    const items: AppSettingsMenuItem[] = [];

    if (customerProvisioning) {
      items.push({
        text: "Customer Provisioning",
        onClick: () => {
          history.push("/customerProvisioning");
        },
      });
    }

    if (isImpersonating) {
      items.push({
        text: "Customer Provisioning",
        onClick: () => {
          history.push("/customerProvisioning");
          removeImpersonationFromLS();
        },
      });

      items.push({
        text: "Stop Impersonation",
        onClick: () => {
          removeImpersonationFromLS();
        },
      });
    }

    if (
      isAllowed(userActionsEnum.VIEW_UNSCOPED_RESOURCES) // we use the VIEW_UNSCOPED_RESOURCES until the relevant user actions are available
    ) {
      items.push({
        text: "Activity Log",
        onClick: () => {
          history.push("/systemHistory");
        },
      });
    }

    items.push({
      text: "Download Manager",
      onClick: () => {
        history.push("/downloadManager");
      },
    });

    items.push({
      text: "Dark Mode",
      keepMenuOpen: true,
      component: (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CheckBox
            wrapperClassName="theme-toggle-menu-item"
            data-testid="theme-toggle-checkbox"
            checked={currentTheme === "DARK"}
            onChange={handleThemeToggle}
            label="Dark Mode"
            variant={CheckBoxVariant.TOGGLE}
          />
        </div>
      ),
    });

    if (hasMultiAccounts && !isImpersonating) {
      items.push({
        text: "Switch Account",
        onClick: () => {
          clearOrgAndAccount();
          removeImpersonationFromLS();
        },
      });
    }

    items.push({
      type: "separator",
      className: "menu-separator",
    });

    items.push({
      component: (
        <div className="logout-button">
          <i className="seem-icon seem-icon-logout" />
          <span>Logout</span>
        </div>
      ),
      onClick: logout,
    });

    return items;
  }, [
    customerProvisioning,
    isImpersonating,
    isAllowed,
    history,
    removeImpersonationFromLS,
    currentTheme,
    handleThemeToggle,
    hasMultiAccounts,
    clearOrgAndAccount,
    logout,
  ]);

  const loggedUserLabel = useMemo(() => {
    const { username, account, context } = authState;
    if (!username) {
      return "";
    }
    if (isImpersonating) {
      return `${context?.customerName} (logged as ${username})`;
    }
    if (hasMultiAccounts) {
      return `${account?.name} (${username})`;
    }
    return username;
  }, [authState, hasMultiAccounts, isImpersonating]);

  return { menuItems, loggedUserLabel };
};

export default useUserMenuItems;
